import React from 'react';
import { Button, Divider, Form, Grid, Icon, Label, Segment } from 'semantic-ui-react';
import { TranslatableRichTextField } from '@screens/offers/NewOfferForm/components';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from '@translations/translationNamespaces';

const SuccessStoryElementItem = ({
  onChange,
  element,
  elementErrors,
  isViewMode,
  isArrowUpDisabled,
  isArrowDownDisabled,
  sectionNumber,
  elementIndex,
  elementPosition,
}) => {
  const { t } = useTranslation(TRANSLATIONS.OFFERS);

  const onArrowUp = () =>
    onChange(prevState => ({
      offerSections: prevState.offerSections.reduce((acc, section) => {
        if (section.position === sectionNumber) {
          return acc.concat([
            {
              ...section,
              elements: section.elements.reduce((elementsAcc, el) => {
                if (elementPosition === el.position) {
                  return elementsAcc.concat([
                    {
                      ...el,
                      items: el.items.reduce((innerElementsAcc, innerElement) => {
                        const prevItem = el.items.find(item => item.position === element.position - 1);
                        const matchingItem = el.items.find(item => item.position === element.position);

                        if (innerElement.position === prevItem.position) {
                          return innerElementsAcc.concat({ ...matchingItem, position: prevItem.position });
                        }
                        if (innerElement.position === matchingItem.position) {
                          return innerElementsAcc.concat({ ...prevItem, position: matchingItem.position });
                        }
                        return innerElementsAcc.concat([innerElement]);
                      }, []),
                    },
                  ]);
                }
                return elementsAcc.concat([el]);
              }, []),
            },
          ]);
        }
        return acc.concat([section]);
      }, []),
    }));

  const onArrowDown = () =>
    onChange(prevState => ({
      offerSections: prevState.offerSections.reduce((acc, section) => {
        if (section.position === sectionNumber) {
          return acc.concat([
            {
              ...section,
              elements: section.elements.reduce((elementsAcc, el) => {
                if (elementPosition === el.position) {
                  return elementsAcc.concat([
                    {
                      ...el,
                      items: el.items.reduce((innerElementsAcc, innerElement) => {
                        const nextItem = el.items.find(item => item.position === element.position + 1);
                        const matchingItem = el.items.find(item => item.position === element.position);

                        if (innerElement.position === matchingItem.position) {
                          return innerElementsAcc.concat({ ...nextItem, position: matchingItem.position });
                        }
                        if (innerElement.position === nextItem.position) {
                          return innerElementsAcc.concat({ ...matchingItem, position: nextItem.position });
                        }
                        return innerElementsAcc.concat([innerElement]);
                      }, []),
                    },
                  ]);
                }
                return elementsAcc.concat([el]);
              }, []),
            },
          ]);
        }
        return acc.concat([section]);
      }, []),
    }));

  const onRemoveItem = () => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Are you sure you want to remove success story item? Item changes will be discarded.`)) {
      onChange(prevState => ({
        offerSections: prevState.offerSections.reduce((acc, section) => {
          if (section.position === sectionNumber) {
            return acc.concat([
              {
                ...section,
                elements: section.elements.reduce((elementsAcc, el) => {
                  if (elementPosition === el.position) {
                    return elementsAcc.concat([
                      {
                        ...el,
                        items: el.items.reduce((innerElementsAcc, innerElement) => {
                          if (innerElement.position === element.position) {
                            return innerElementsAcc;
                          }
                          if (innerElement.position > element.position) {
                            return innerElementsAcc.concat([{ ...innerElement, position: innerElement.position - 1 }]);
                          }
                          return innerElementsAcc.concat([innerElement]);
                        }, []),
                      },
                    ]);
                  }
                  return elementsAcc.concat([el]);
                }, []),
              },
            ]);
          }
          return acc.concat([section]);
        }, []),
      }));
    }
  };

  const onTranslatableContentChange = (e, { name, value, language }) =>
    onChange(prevState => ({
      offerSections: prevState.offerSections.reduce((acc, section) => {
        if (section.position === sectionNumber) {
          return acc.concat([
            {
              ...section,
              elements: section.elements.reduce((elementsAcc, el) => {
                if (elementPosition === el.position) {
                  return elementsAcc.concat([
                    {
                      ...el,
                      items: el.items.reduce((innerElementsAcc, innerElement) => {
                        if (innerElement.position === element.position) {
                          return innerElementsAcc.concat([
                            {
                              ...innerElement,
                              [name]: {
                                ...innerElement[name],
                                [language]: value,
                              },
                            },
                          ]);
                        }
                        return innerElementsAcc.concat([innerElement]);
                      }, []),
                    },
                  ]);
                }
                return elementsAcc.concat([el]);
              }, []),
            },
          ]);
        }
        return acc.concat([section]);
      }, []),
    }));

  return (
    <Segment>
      <Form.Group>
        <Grid container stackable columns={1} divided>
          <Grid.Column>
            <div className="d-flex align-items-center justify-content-between h-100">
              <div className="d-flex align-items-center h-100">
                <Label>
                  {t('successStory.item')} #{element.position}
                </Label>
                <Button icon className="mr-2 ml-2" disabled={isArrowUpDisabled} onClick={onArrowUp}>
                  <Icon name="arrow up" />
                </Button>
                <Button icon className="mr-2 ml-2" disabled={isArrowDownDisabled} onClick={onArrowDown}>
                  <Icon name="arrow down" />
                </Button>
              </div>

              <div className="d-flex align-items-center h-100">
                <Button className="mr-2 ml-2" icon color="red" onClick={onRemoveItem}>
                  <Icon name="trash" />
                </Button>
              </div>
            </div>
          </Grid.Column>
        </Grid>
      </Form.Group>
      <Divider />
      <Form.Group>
        <Grid container stackable columns={2} divided>
          <TranslatableRichTextField
            type="text"
            name="title"
            label={t('successStory.title')}
            errorMessage={elementErrors?.title}
            value={element?.title}
            icon="font"
            onChange={onTranslatableContentChange}
            disabled={isViewMode}
          />
        </Grid>
      </Form.Group>
      <Form.Group>
        <Grid container stackable columns={2} divided>
          <TranslatableRichTextField
            type="text"
            name="description"
            label={t('successStory.description')}
            errorMessage={elementErrors?.description}
            value={element?.description}
            icon="font"
            onChange={onTranslatableContentChange}
            disabled={isViewMode}
          />
        </Grid>
      </Form.Group>
    </Segment>
  );
};

export default SuccessStoryElementItem;
